import type { MeasurementStatus } from '@/api/generator';
import { SECONDS_PER_MINUTE, formatHoursMinutes } from '@/utils/durationUtils';
import { t } from 'i18next';
import type { ZeroMeasurementValidity } from './useLatestZeroMeasurement';
import type { ZeroMeasurementButtonPhase } from './zeroMeasurementStartButton';

export interface ZeroMeasurementButtonTextsInput {
  phase: ZeroMeasurementButtonPhase;
  /** Status of the running zero measurement; only read while measuring. */
  status?: MeasurementStatus | null;
  /** The progress as shown in the hub, e.g. "32%"; only read while measuring. */
  progressText: string;
  /** Validity of the latest zero measurement; only read while idle. */
  validity?: ZeroMeasurementValidity;
  /** Why a zero measurement cannot start; only read while idle. */
  blockedReason?: string;
}

export interface ZeroMeasurementButtonTexts {
  /**
   * The status of the run: Initializing until the device reports
   * ZeroMeasuring (including the pending start), Finished once succeeded,
   * none while idle.
   */
  runStatus?: MeasurementStatus;
  /**
   * The ring text: the run status in short form while it runs and once it
   * succeeded, the name of the measurement otherwise. Uppercase on the
   * curve: same glyph height all along, so the ring reads clean.
   */
  ringLabel: string;
  /**
   * Tooltip lines: running, the status in its long form, the progress and
   * the cancel action; succeeded, the name and completed; otherwise the
   * validity (if any) above the action, or above the reason the action is
   * blocked.
   */
  tooltipLines: string[];
  /** The accessible name: the tooltip lines joined into one sentence. */
  label: string;
}

/** The remaining validity, down to the second during the last minute. */
export function remainingText(seconds: number): string {
  return seconds < SECONDS_PER_MINUTE ? `${Math.ceil(seconds)} s` : formatHoursMinutes(seconds);
}

/** The texts the zero measurement start button shows and speaks in a phase. */
export function zeroMeasurementButtonTexts(
  input: ZeroMeasurementButtonTextsInput,
): ZeroMeasurementButtonTexts {
  const runStatus = runStatusOf(input);
  const ringLabel = (
    runStatus
      ? t(`ZeroMeasurementStatusShort.${runStatus}` as 'ZeroMeasurementStatusShort.Initializing')
      : t('ZeroMeasurement')
  ).toLocaleUpperCase();
  const tooltipLines = tooltipLinesOf(input, runStatus);
  return { runStatus, ringLabel, tooltipLines, label: tooltipLines.join(', ') };
}

function runStatusOf({ phase, status }: ZeroMeasurementButtonTextsInput) {
  if (phase === 'measuring') {
    return status === 'ZeroMeasuring' ? 'ZeroMeasuring' : 'Initializing';
  }
  return phase === 'succeeded' ? 'Finished' : undefined;
}

function tooltipLinesOf(
  { phase, progressText, validity, blockedReason }: ZeroMeasurementButtonTextsInput,
  runStatus: MeasurementStatus | undefined,
) {
  if (phase === 'measuring') {
    return [
      t(`MeasurementStatus.${runStatus ?? 'Initializing'}` as 'MeasurementStatus.Initializing'),
      progressText,
      t('CancelZeroMeasurement'),
    ];
  }
  if (phase === 'succeeded') {
    return [t('ZeroMeasurement'), t('ZeroMeasurementCompleted')];
  }
  const action = blockedReason ?? t('StartZeroMeasurement');
  return validity
    ? [t('ZeroMeasurementValidFor', { duration: remainingText(validity.remainingSeconds) }), action]
    : [action];
}
